import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Routes, Route } from 'react-router-dom';
import iconPng from '/icon.png';
import { useAuthStore } from './store/authStore';
import AuthForm from './components/auth/AuthForm';
import Sidebar from './components/layout/Sidebar';
import MessageList from './components/chat/MessageList';
import DirectMessageChat from './components/chat/DirectMessageChat';
import FriendsList from './components/friends/FriendsList';
import VoiceChannel from './components/voice/VoiceChannel';
import HomeChannel from './components/minecraft/HomeChannel';
import BannedServerScreen from './components/server/BannedServerScreen';
import GamingHub from './components/gaming/GamingHub';
import GameDownloadsOverlay from './components/gaming/GameDownloadsOverlay';
import { useGamingStore } from './store/gamingStore';
import { useAdminStore } from './store/adminStore';
import AdminConnect from './components/admin/AdminConnect';
import { useServerStore } from './store/serverStore';
import { useMessageStore } from './store/messageStore';
import { useVoiceStore } from './store/voiceStore';
import IncomingCallNotification from './components/layout/IncomingCallNotification';
import ToastContainer from './components/layout/ToastContainer';
import PaymentDrawer from './components/billing/PaymentDrawer';
import UpdateNotification from './components/layout/UpdateNotification';
import UpdateScreen from './components/layout/UpdateScreen';
import Header from './components/layout/Header';
import InviteLink from './components/invitation/InviteLink';
import ServerMembersPanel from './components/server/ServerMembersPanel';
import { 
  ServerOff, 
  RefreshCw, 
  Clock, 
  Activity, 
  RotateCw, 
  MessageSquare,
  Ban,
  PauseCircle,
  Bell
} from 'lucide-react';
import { useNotificationStore } from './store/notificationStore';
import { useFriendStore } from './store/friendStore';
import { Skeleton } from './components/ui/Skeleton';
import WarningPopup, { PendingWarning } from './components/moderation/WarningPopup';
import { getChannel, joinUserChannel } from './lib/realtime';
import NotificationCenter from './components/layout/NotificationCenter';

// Custom styles for Electron window controls
const electronStyles = {
  appDrag: {
    WebkitAppRegion: 'drag'
  } as React.CSSProperties,
  appNoDrag: {
    WebkitAppRegion: 'no-drag'
  } as React.CSSProperties
};

const App: React.FC = () => {
  const { user, fetchProfile } = useAuthStore();
  const { currentServer, currentChannel, isLoading: serverLoading } = useServerStore();
  const { currentDmUser, isLoading: messageLoading } = useMessageStore();
  const { isGamingHubActive, initGameDownloadEvents } = useGamingStore();
  const { isAdminConnectActive } = useAdminStore();
  const { isCallIncoming } = useVoiceStore();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isElectron] = useState(() => !!window.api);
  const [isMac] = useState(() => window.api?.platform === 'darwin');
  const { unreadCount } = useNotificationStore();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const notificationBtnRef = useRef<HTMLButtonElement>(null);
  const [isBanned, setIsBanned] = useState(false);
  const [banReason, setBanReason] = useState<string | null>(null);
  const [banId, setBanId] = useState<string | null>(null);
  const [bannedAt, setBannedAt] = useState<string | null>(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspendedUntil, setSuspendedUntil] = useState<string | null>(null);
  const [suspensionId, setSuspensionId] = useState<string | null>(null);
  const [pendingWarnings, setPendingWarnings] = useState<PendingWarning[]>([]);
  const [fullscreenUpdate, setFullscreenUpdate] = useState<any>(null);
  const [isMemberPanelOpen, setIsMemberPanelOpen] = useState(false);
  const fullscreenUpdateRef = React.useRef<any>(null);
  React.useEffect(() => { fullscreenUpdateRef.current = fullscreenUpdate; }, [fullscreenUpdate]);

  // Listen for startup update (fullscreen mode)
  useEffect(() => {
    if (!window.api?.onUpdateStatus) return;
    const cleanup = window.api.onUpdateStatus((data: any) => {
      if (data.startup && ['available', 'downloading', 'ready'].includes(data.status)) {
        setFullscreenUpdate(data);
      } else if (['downloading', 'ready'].includes(data.status) && fullscreenUpdateRef.current) {
        // keep fullscreen updated for manual downloads triggered from UpdateNotification
        setFullscreenUpdate(data);
      } else if (data.status === 'error' && fullscreenUpdateRef.current) {
        setFullscreenUpdate(data);
      } else if (data.status === 'up-to-date') {
        setFullscreenUpdate(null);
      }
    });
    return cleanup;
  }, []);

  // Close notification dropdown on click outside
  useEffect(() => {
    if (!showNotifications) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        notificationRef.current && !notificationRef.current.contains(e.target as Node) &&
        notificationBtnRef.current && !notificationBtnRef.current.contains(e.target as Node)
      ) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  // Listen for global ban event dispatched by api interceptor
  useEffect(() => {
    const onBanned = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setIsBanned(true);
      setBanReason(detail?.reason || null);
      setBanId(detail?.ban_id || null);
      setBannedAt(detail?.banned_at || null);
    };
    window.addEventListener('erozion-banned', onBanned);
    return () => window.removeEventListener('erozion-banned', onBanned);
  }, []);

  // Listen for temporary suspension event
  useEffect(() => {
    const onSuspended = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setIsSuspended(true);
      setSuspendedUntil(detail?.until || null);
      setSuspensionId(detail?.moderation_id || null);
    };
    window.addEventListener('erozion-suspended', onSuspended);
    return () => window.removeEventListener('erozion-suspended', onSuspended);
  }, []);

  // Fetch pending warnings on login + listen for real-time warnings via socket
  useEffect(() => {
    if (!user) return;
    const apiBase = import.meta.env.VITE_API_URL || '/api';
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

    fetch(`${apiBase}/me/warnings/pending`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then((ws: PendingWarning[]) => { if (ws.length) setPendingWarnings(ws); })
      .catch(() => {});

    const userId = localStorage.getItem('user_id');
    if (!userId) return;
    
    // Ensure user channel is joined
    joinUserChannel(userId);
    const userChannel = getChannel(`user:${userId}`);
    
    const warningRef = userChannel?.on('warning_received', (w: PendingWarning) => {
      setPendingWarnings(prev => {
        if (prev.some(p => p.id === w.id)) return prev;
        return [...prev, w];
      });
    });

    const appealAcceptRef = userChannel?.on('appeal_accepted', (data: { appeal_id: string; action_type: string; admin_response: string | null }) => {
      window.dispatchEvent(new CustomEvent('erozion-appeal-accepted', { detail: data }));
    });
    const appealRejectRef = userChannel?.on('appeal_rejected', (data: { appeal_id: string; action_type: string; admin_response: string | null }) => {
      window.dispatchEvent(new CustomEvent('erozion-appeal-rejected', { detail: data }));
    });

    // Immediately enforce bans/suspensions applied while the user is online
    const forceRef = userChannel?.on('force_disconnect', (data: { reason: 'banned' | 'suspended'; message: string; until: string | null; action_id: string }) => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      if (data.reason === 'banned') {
        setIsBanned(true);
        setBanReason(data.message);
        setBanId(data.action_id);
        setBannedAt(new Date().toISOString());
      } else if (data.reason === 'suspended') {
        setIsSuspended(true);
        setSuspendedUntil(data.until);
        setSuspensionId(data.action_id);
      }
    });

    const interactionRef = userChannel?.on('interaction_response', (data: any) => {
      window.dispatchEvent(new CustomEvent('erozion-interaction-response', { detail: data }));
    });

    return () => {
      if (userChannel) {
        if (warningRef !== undefined) userChannel.off('warning_received', warningRef);
        if (appealAcceptRef !== undefined) userChannel.off('appeal_accepted', appealAcceptRef);
        if (appealRejectRef !== undefined) userChannel.off('appeal_rejected', appealRejectRef);
        if (forceRef !== undefined) userChannel.off('force_disconnect', forceRef);
        if (interactionRef !== undefined) userChannel.off('interaction_response', interactionRef);
      }
    };
  }, [user]);
  
  // Setup invite processing for Electron client
  useEffect(() => {
    if (window.api?.onProcessInvite) {
      const cleanup = window.api.onProcessInvite((inviteCode) => {
        // Process the invitation here (logic to display and accept)
        console.log('Processing invitation from deep link:', inviteCode);
        // Redirect to the invite acceptance page
        window.location.hash = `/invite/${inviteCode}`;
      });
      
      return () => {
        if (typeof cleanup === 'function') cleanup();
      };
    }
  }, []);

  // Handle game launch deep links (erozion://launch/<slug>)
  useEffect(() => {
    if (window.api?.onProcessLaunchGame) {
      const cleanup = window.api.onProcessLaunchGame(({ gameSlug, reviewStoreVersionId }) => {
        if (!gameSlug) return;
        useGamingStore.getState().openDownloadedGame(gameSlug, {
          reviewStoreVersionId: reviewStoreVersionId ?? undefined,
        }).catch(console.error);
      });

      return () => {
        if (typeof cleanup === 'function') cleanup();
      };
    }
  }, []);

  // Handle OIDC auth callback from Electron deep link
  useEffect(() => {
    if (window.api?.onAuthCallback) {
      const cleanup = window.api.onAuthCallback(({ code, state, error }) => {
        if (error) {
          console.error('OIDC error from provider:', error);
          return;
        }
        if (code && state) {
          useAuthStore.getState().handleOIDCCallback(code, state).catch(console.error);
        }
      });
      return () => {
        if (typeof cleanup === 'function') cleanup();
      };
    }
  }, []);

  // Initialize native game download events in Electron
  useEffect(() => {
    if (isElectron) {
      initGameDownloadEvents();
    }
  }, [isElectron, initGameDownloadEvents]);
  
  // Detect content loading state
  const isContentLoading = serverLoading || messageLoading;

  useEffect(() => {
    // Check if API is available (only in web mode)
    const checkApi = async () => {
      try {
        const apiBase = (import.meta.env.VITE_API_URL || 'https://api.dev.erozion.net/api').replace(/\/api\/?$/, '');
        const response = await fetch(`${apiBase}/health`);
        if (!response.ok) {
          setApiError('API server is not responding. Please make sure the server is running.');
        }
      } catch (error) {
        setApiError('Cannot connect to the API server. Please make sure the server is running.');
      }
    };
    
    // Only check API in web mode
    if (!isElectron) {
      checkApi();
    }
    
    // Check if user is already logged in
    if (user) {
      fetchProfile();
      if (localStorage.getItem('access_token')) {
        const notificationStore = useNotificationStore.getState();
        notificationStore.fetchNotifications();
        notificationStore.fetchUnreadCount();
        const unsubNotifications = notificationStore.subscribeToNotifications();

        // Subscribe to friend requests globally
        const friendStore = useFriendStore.getState();
        friendStore.fetchFriends();
        friendStore.fetchPendingRequests();
        const unsubFriends = friendStore.subscribeToFriendRequests();

        // Fetch unread DM counts
        const messageStore = useMessageStore.getState();
        messageStore.fetchUnreadDms();
        messageStore.fetchConversations();

        // Request OS notification permission early
        if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'default') {
          window.Notification.requestPermission();
        }
        return () => { unsubNotifications?.(); unsubFriends?.(); };
      }
    }
  }, [user, fetchProfile, isElectron]);

  useEffect(() => {
    if (!user || !currentServer?.id) return;
    return useServerStore.getState().subscribeToServerEvents();
  }, [user, currentServer?.id]);

  // Suspended user screen (temporary)
  if (isSuspended) {
    const formattedUntil = suspendedUntil
      ? new Date(suspendedUntil).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
      : null;
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="max-w-md w-full animate-fade-in">
          <div className="h-1.5 bg-orange-500 rounded-t-xl" />
          <div className="bg-bg-secondary border border-border-color border-t-0 rounded-b-xl overflow-hidden">
            <div className="bg-orange-500/10 px-6 py-5 border-b border-orange-500/20 flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
                <PauseCircle className="h-6 w-6 text-orange-400" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-orange-400/70 mb-0.5">Erozion</p>
                <h1 className="text-xl font-bold text-text-primary">Compte suspendu temporairement</h1>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-text-secondary text-sm">
                Votre accès à Erozion a été temporairement suspendu suite à une violation de nos conditions d'utilisation.
              </p>
              {formattedUntil && (
                <div className="rounded-lg border border-border-color bg-bg-primary p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1">Levée de suspension</p>
                  <p className="text-text-primary text-sm font-medium">{formattedUntil}</p>
                </div>
              )}
              {suspensionId && (
                <div className="rounded-lg border border-border-color bg-bg-primary p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1">ID de modération</p>
                  <p className="text-text-primary text-xs font-mono" title={suspensionId}>{suspensionId.slice(0, 8).toUpperCase()}</p>
                </div>
              )}
              <p className="text-xs text-text-muted">
                Si vous pensez qu'il s'agit d'une erreur, contactez{' '}
                <a href={`mailto:help@erozion.net?subject=Contestation%20suspension%20${suspensionId}`} className="text-accent-color hover:underline">
                  help@erozion.net
                </a>{' '}en mentionnant votre ID de modération.
              </p>
            </div>
            <div className="px-6 pb-5">
              <button onClick={() => useAuthStore.getState().signOut()} className="w-full btn btn-secondary text-sm">
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Banned user screen
  if (isBanned) {
    const formattedDate = bannedAt
      ? new Date(bannedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
      : null;
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="max-w-md w-full animate-fade-in">
          {/* Red top bar */}
          <div className="h-1.5 bg-error rounded-t-xl" />
          <div className="bg-bg-secondary border border-border-color border-t-0 rounded-b-xl overflow-hidden">
            {/* Header */}
            <div className="bg-error/10 px-6 py-5 border-b border-error/20 flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-error/20 border border-error/30 flex items-center justify-center">
                <Ban className="h-6 w-6 text-error" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-error/70 mb-0.5">Erozion</p>
                <h1 className="text-xl font-bold text-text-primary">Compte suspendu</h1>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <p className="text-text-secondary text-sm">
                Votre accès à Erozion a été suspendu de manière permanente suite à une violation de nos conditions d'utilisation.
              </p>

              {/* Motif */}
              <div className="rounded-lg border border-border-color bg-bg-primary p-4 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Motif</p>
                <p className="text-text-primary text-sm font-medium">{banReason || 'Non respect des CGU'}</p>
              </div>

              {/* Meta info */}
              <div className="grid grid-cols-2 gap-3">
                {formattedDate && (
                  <div className="rounded-lg border border-border-color bg-bg-primary p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1">Date</p>
                    <p className="text-text-primary text-xs">{formattedDate}</p>
                  </div>
                )}
                {banId && (
                  <div className="rounded-lg border border-border-color bg-bg-primary p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1">ID de ban</p>
                    <p className="text-text-primary text-xs font-mono truncate" title={banId}>{banId.slice(0, 8).toUpperCase()}</p>
                  </div>
                )}
              </div>

              {/* Contact */}
              <p className="text-xs text-text-muted">
                Si vous pensez qu'il s'agit d'une erreur, contactez{' '}
                <a href={`mailto:help@erozion.net?subject=Contestation%20de%20ban%20${banId}`} className="text-accent-color hover:underline">
                  help@erozion.net
                </a>{' '}en mentionnant votre ID de ban.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5">
              <button
                onClick={() => useAuthStore.getState().signOut()}
                className="w-full btn btn-secondary text-sm"
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // API error display for web mode
  if (apiError && !isElectron) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="card max-w-md w-full animate-fade-in overflow-hidden">
          <div className="relative bg-gradient-to-br from-bg-tertiary to-bg-secondary p-8 -mt-4 -mx-4 mb-6 flex flex-col items-center justify-center border-b border-border-color">
            <div className="absolute top-0 left-0 w-full h-full opacity-5">
              <div className="absolute inset-0 flex items-center justify-center"></div>
            </div>
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center p-3 bg-bg-primary bg-opacity-60 rounded-full mb-3 backdrop-blur-sm border border-border-color">
                <ServerOff className="h-10 w-10 text-error" />
              </div>
              <h1 className="text-2xl font-bold text-accent-color mb-1">Connection Lost</h1>
              <p className="text-text-secondary">We're having trouble reaching Erozion servers</p>
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-text-primary mb-2">What happened?</h2>
              <p className="text-text-secondary">{apiError}</p>
            </div>
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-text-primary">Try these steps:</h2>
              <ul className="text-text-secondary space-y-2">
                <li className="flex items-start">
                  <RefreshCw className="h-5 w-5 mr-2 flex-shrink-0 text-accent-color" />
                  <span>Check if you're connected to the internet</span>
                </li>
                <li className="flex items-start">
                  <Clock className="h-5 w-5 mr-2 flex-shrink-0 text-accent-color" />
                  <span>Wait a few moments and try again</span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col space-y-3">
              <a 
                href="https://status.erozion.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary flex items-center justify-center"
              >
                <Activity className="h-5 w-5 mr-2" />
                Check Erozion Status Page
              </a>
              <button 
                onClick={() => window.location.reload()}
                className="btn btn-primary flex items-center justify-center"
              >
                <RotateCw className="h-5 w-5 mr-2" />
                Refresh and Try Again
              </button>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-border-color flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-7 h-7 rounded-full bg-accent-color flex items-center justify-center mr-2">
                <MessageSquare className="h-4 w-4 text-bg-primary" />
              </div>
              <span className="font-semibold text-text-primary">Erozion</span>
            </div>
            <p className="text-xs text-text-muted">
              Support: <a href="mailto:help@erozion.net" className="text-accent-color hover:underline">help@erozion.net</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col h-screen">
        {isElectron && (
          <div className="h-9 bg-black/30 backdrop-blur-md flex items-center justify-between" style={electronStyles.appDrag}>
            {isMac ? (
              <div className="w-20" />
            ) : (
              <div />
            )}
            {!isMac && (
              <div className="flex h-full" style={electronStyles.appNoDrag}>
                <button 
                  onClick={() => window.api?.minimizeWindow()}
                  className="px-4 flex items-center justify-center text-white hover:bg-white/10"
                >
                  &#8211;
                </button>
                <button 
                  onClick={() => window.api?.maximizeWindow()}
                  className="px-4 flex items-center justify-center text-white hover:bg-white/10"
                >
                  □
                </button>
                <button 
                  onClick={() => window.api?.closeWindow()}
                  className="px-4 flex items-center justify-center text-white hover:bg-red-500"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}
        <Routes>
          <Route path="/invite/:inviteCode" element={<InviteLink />} />
          <Route path="*" element={<AuthForm />} />
        </Routes>
        {isElectron && fullscreenUpdate && <UpdateScreen update={fullscreenUpdate} />}
        {isElectron && <UpdateNotification onStartFullscreen={(data) => setFullscreenUpdate(data)} />}
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-bg-primary">
      {/* System bar — full width at top */}
      {isElectron && (
        <div className="h-10 bg-bg-primary/60 flex items-center justify-between relative flex-shrink-0" style={electronStyles.appDrag}>
          {isMac ? (
            <div className="w-20" />
          ) : (
            <div />
          )}
          
          {/* Center label */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 pointer-events-none">
            {isAdminConnectActive ? (
              <>
                <img src={iconPng} alt="Erozion" className="w-4 h-4 rounded object-cover" />
                <span className="text-xs text-text-primary font-medium tracking-wide">Admin Connect</span>
              </>
            ) : isGamingHubActive ? (
              <>
                <img src={iconPng} alt="Erozion" className="w-4 h-4 rounded object-cover" />
                <span className="text-xs text-text-secondary font-medium tracking-wide">Gaming Hub</span>
              </>
            ) : currentServer ? (
              <>
                {currentServer.icon_url ? (
                  <img src={currentServer.icon_url} alt={currentServer.name} className="w-4 h-4 rounded object-cover" />
                ) : (
                  <div className="w-4 h-4 rounded bg-accent-color text-white flex items-center justify-center text-[8px] font-bold">
                    {currentServer.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-xs text-text-secondary font-medium truncate max-w-[200px]">{currentServer.name}</span>
              </>
            ) : (
              <>
                <img src={iconPng} alt="Erozion" className="w-4 h-4 rounded object-cover" />
                <span className="text-xs text-text-secondary font-medium tracking-wide">Messages privés</span>
              </>
            )}
          </div>

          <div className="flex items-center h-full gap-1 pr-2" style={electronStyles.appNoDrag}>
            <div className="relative">
              <button 
                ref={notificationBtnRef}
                className={`relative w-7 h-7 rounded-lg flex items-center justify-center transition-all ${showNotifications ? 'text-accent-color bg-accent-color/10' : 'text-text-muted hover:text-text-primary hover:bg-highlight/50'}`}
                onClick={() => setShowNotifications(!showNotifications)}
                title="Notifications"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-error text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && ReactDOM.createPortal(
                <div 
                  ref={notificationRef}
                  className="fixed w-80 bg-bg-secondary border border-border-color rounded-md shadow-lg z-[9999]"
                  style={{ top: '2.5rem', right: '0.5rem' }}
                >
                  <NotificationCenter onClose={() => setShowNotifications(false)} />
                </div>,
                document.body
              )}
            </div>
            {!isMac && (
              <>
                <button 
                  onClick={() => window.api?.minimizeWindow()}
                  className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 rounded-lg transition-all text-sm"
                >
                  &#8211;
                </button>
                <button 
                  onClick={() => window.api?.maximizeWindow()}
                  className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 rounded-lg transition-all text-xs"
                >
                  □
                </button>
                <button 
                  onClick={() => window.api?.closeWindow()}
                  className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-white hover:bg-error rounded-lg transition-all text-sm"
                >
                  ✕
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <Routes>
        <Route path="/invite/:inviteCode" element={<InviteLink />} />
        <Route path="*" element={
          <>
            <div className="flex flex-1 overflow-hidden">
              <Sidebar onToggleMembers={() => setIsMemberPanelOpen((open) => !open)} />
              <div className="flex-1 flex flex-col overflow-hidden">
                {!isGamingHubActive && !isAdminConnectActive && (
                  <Header
                    isMemberPanelOpen={isMemberPanelOpen}
                    onToggleMembers={() => setIsMemberPanelOpen((open) => !open)}
                  />
                )}
                <main className="flex-1 flex flex-col overflow-hidden relative">
                  <div className="flex-1 overflow-hidden">
                    {isAdminConnectActive ? (
                      <AdminConnect />
                    ) : isGamingHubActive ? (
                      <GamingHub />
                    ) : currentServer && currentServer.is_banned ? (
                      <BannedServerScreen serverName={currentServer.name} />
                    ) : currentServer ? (
                      currentChannel?.type === 'text' ? (
                        <MessageList />
                      ) : currentChannel?.type === 'voice' ? (
                        <VoiceChannel />
                      ) : (
                        <HomeChannel serverId={currentServer.id} />
                      )
                    ) : currentDmUser ? (
                      <DirectMessageChat />
                    ) : isContentLoading ? (
                      <div className="h-full p-4 flex flex-col">
                        <Skeleton className="w-full h-12 mb-4" />
                        <Skeleton className="w-full h-12 mb-4" />
                        <Skeleton className="w-3/4 h-12 mb-4" />
                        <Skeleton className="w-1/2 h-12" />
                      </div>
                    ) : (
                      <FriendsList />
                    )}
                  </div>
                </main>
              </div>
              {!isGamingHubActive && !isAdminConnectActive && currentServer && isMemberPanelOpen && (
                <ServerMembersPanel serverId={currentServer.id} />
              )}
            </div>
            {isElectron && <GameDownloadsOverlay />}
          </>
        } />
      </Routes>
      
      {isCallIncoming && <IncomingCallNotification />}
      {isElectron && fullscreenUpdate && <UpdateScreen update={fullscreenUpdate} />}
      {isElectron && <UpdateNotification onStartFullscreen={(data) => setFullscreenUpdate(data)} />}
      <ToastContainer />
      <PaymentDrawer />
      {pendingWarnings.length > 0 && (
        <WarningPopup
          warnings={pendingWarnings}
          onAcknowledge={(id) => setPendingWarnings(prev => prev.filter(w => w.id !== id))}
        />
      )}
    </div>
  );
};

export default App;
